import { useEffect, useState } from "react";

type CapturedError = {
  id: number;
  message: string;
  file?: string;
  line?: number;
  col?: number;
  stack?: string;
  kind: "error" | "promise" | "chunk";
  at: number;
};

const RELOAD_FLAG = "__lov_chunk_reload";
const RELOAD_INFO = "__lov_chunk_reload_info";

type ReloadInfo = {
  message: string;
  assetUrl?: string;
  retryUrl: string;
  fromPage: string;
  at: number;
};

function parseStackLocation(stack?: string): { file?: string; line?: number; col?: number } {
  if (!stack) return {};
  // Try several common patterns: "at fn (file:line:col)", "file:line:col"
  const lines = stack
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  for (const l of lines) {
    const m =
      l.match(/\(?(https?:\/\/[^\s)]+|\/[^\s)]+|[a-zA-Z]:[^\s)]+):(\d+):(\d+)\)?/) ||
      l.match(/(\S+\.[tj]sx?):(\d+):(\d+)/);
    if (m) {
      return { file: m[1], line: Number(m[2]), col: Number(m[3]) };
    }
  }
  return {};
}

function isChunkLoadError(msg: string): boolean {
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed|Unexpected token/i.test(
    msg,
  );
}

function cacheBustReload(info?: { message: string; assetUrl?: string }) {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return; // avoid reload loops
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", String(Date.now()));
  const retryUrl = url.toString();
  try {
    const payload: ReloadInfo = {
      message: info?.message ?? "Manueller Cache-Bust",
      assetUrl: info?.assetUrl,
      retryUrl,
      fromPage: window.location.href,
      at: Date.now(),
    };
    sessionStorage.setItem(RELOAD_INFO, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  window.location.replace(retryUrl);
}

export function DevErrorOverlay() {
  const [errors, setErrors] = useState<CapturedError[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [lastReload, setLastReload] = useState<ReloadInfo | null>(null);

  useEffect(() => {
    // Surface the previous cache-bust's failing asset/retry URL after reload.
    try {
      const raw = sessionStorage.getItem(RELOAD_INFO);
      if (raw) {
        const info = JSON.parse(raw) as ReloadInfo;
        // Only show recent reloads (last 60s) to avoid stale noise.
        if (Date.now() - info.at < 60_000) setLastReload(info);
        sessionStorage.removeItem(RELOAD_INFO);
      }
    } catch {
      /* ignore */
    }

    // Clear the reload flag once the page loaded successfully for a few seconds.
    const clearTimer = window.setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* ignore */
      }
    }, 5000);

    let idCounter = 0;
    const push = (e: Omit<CapturedError, "id" | "at">) => {
      idCounter += 1;
      const full: CapturedError = { ...e, id: idCounter, at: Date.now() };
      setErrors((prev) => [full, ...prev].slice(0, 10));
      // Auto cache-bust on stale-bundle / chunk-load failures
      if (e.kind === "chunk" || isChunkLoadError(e.message)) {
        cacheBustReload({ message: e.message, assetUrl: e.file });
      }
    };

    const onError = (ev: ErrorEvent) => {
      const target = ev.target as (HTMLElement & { src?: string; href?: string }) | null;
      if (target && (target.tagName === "SCRIPT" || target.tagName === "LINK")) {
        if (
          target.tagName === "LINK" &&
          (target.getAttribute("rel") ?? "").toLowerCase() !== "stylesheet"
        ) {
          return;
        }
        // Asset failed to load → likely stale bundle reference
        push({
          kind: "chunk",
          message: `Asset konnte nicht geladen werden: ${target.src || target.href || target.tagName}`,
          file: target.src || target.href,
        });
        return;
      }
      const loc = parseStackLocation(ev.error?.stack);
      push({
        kind: "error",
        message: ev.message || String(ev.error || "Unbekannter Fehler"),
        file: ev.filename || loc.file,
        line: ev.lineno || loc.line,
        col: ev.colno || loc.col,
        stack: ev.error?.stack,
      });
    };

    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : JSON.stringify(reason);
      const stack = reason instanceof Error ? reason.stack : undefined;
      const loc = parseStackLocation(stack);
      push({ kind: "promise", message, ...loc, stack });
    };

    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.clearTimeout(clearTimer);
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  if (errors.length === 0 && !lastReload) return null;

  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 2147483646,
        maxWidth: 460,
        width: "calc(100vw - 24px)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 12,
        color: "#fff",
        background: "#7f1d1d",
        border: "1px solid #fca5a5",
        borderRadius: 8,
        boxShadow: "0 10px 30px rgba(0,0,0,.35)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "#991b1b",
          fontWeight: 700,
        }}
      >
        <span>
          ⚠ {errors.length} Fehler{lastReload ? " · Cache-Bust aktiv" : ""}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            style={btnStyle}
            aria-label={collapsed ? "Details zeigen" : "Details ausblenden"}
          >
            {collapsed ? "▴" : "▾"}
          </button>
          <button
            type="button"
            onClick={() => cacheBustReload({ message: "Manueller Cache-Bust" })}
            style={btnStyle}
            title="Cache leeren und neu laden"
          >
            ↻ Neu laden
          </button>
          <button
            type="button"
            onClick={() => {
              setErrors([]);
              setLastReload(null);
            }}
            style={btnStyle}
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
      </div>
      {!collapsed && (
        <div style={{ maxHeight: 260, overflow: "auto" }}>
          {lastReload && (
            <div
              style={{
                padding: "8px 12px",
                background: "rgba(0,0,0,.2)",
                borderTop: "1px solid rgba(255,255,255,.15)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                ↻ Letzter Cache-Bust ({new Date(lastReload.at).toLocaleTimeString()})
              </div>
              <div style={{ opacity: 0.9 }}>Grund: {lastReload.message}</div>
              {lastReload.assetUrl && (
                <div style={{ opacity: 0.85, marginTop: 2, wordBreak: "break-all" }}>
                  <span style={{ opacity: 0.7 }}>Asset/Chunk:</span> {lastReload.assetUrl}
                </div>
              )}
              <div style={{ opacity: 0.85, marginTop: 2, wordBreak: "break-all" }}>
                <span style={{ opacity: 0.7 }}>Ursprung:</span> {lastReload.fromPage}
              </div>
              <div style={{ opacity: 0.85, marginTop: 2, wordBreak: "break-all" }}>
                <span style={{ opacity: 0.7 }}>Retry:</span> {lastReload.retryUrl}
              </div>
              <button
                type="button"
                onClick={() => {
                  const text = `Grund: ${lastReload.message}\nAsset: ${lastReload.assetUrl ?? "-"}\nUrsprung: ${lastReload.fromPage}\nRetry: ${lastReload.retryUrl}`;
                  void navigator.clipboard?.writeText(text);
                }}
                style={{ ...btnStyle, marginTop: 6 }}
              >
                📋 URLs kopieren
              </button>
            </div>
          )}
          {errors.map((e) => (
            <div
              key={e.id}
              style={{ padding: "8px 12px", borderTop: "1px solid rgba(255,255,255,.15)" }}
            >
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{e.message}</div>
              {e.file && (
                <div style={{ opacity: 0.85 }}>
                  {e.file}
                  {e.line ? `:${e.line}` : ""}
                  {e.col ? `:${e.col}` : ""}
                </div>
              )}
              {e.stack && (
                <details style={{ marginTop: 4 }}>
                  <summary style={{ cursor: "pointer", opacity: 0.85 }}>Stack</summary>
                  <pre
                    style={{
                      margin: "4px 0 0",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      fontSize: 11,
                      opacity: 0.9,
                    }}
                  >
                    {e.stack}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: "rgba(0,0,0,.25)",
  border: "1px solid rgba(255,255,255,.3)",
  color: "#fff",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 11,
  cursor: "pointer",
  fontFamily: "inherit",
};
